/**
 * workbookApi.js
 * Axios-based API services for workbook, metadata, and formula endpoints.
 * All API calls return Promises. Errors are thrown for react-query handling.
 */

import axios from 'axios';
import { headers, baseUrl } from '../../../services/common/credentials';

// ─── API Client ───────────────────────────────────────────────────────────────

const apiClient = axios.create({
  baseURL: `http://154.61.75.209:1200/api/v2/user_modules/`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ── Request interceptor: attach auth token ────────────────────────────────────
apiClient.interceptors.request.use((config) => {
  const userCreds = JSON.parse(sessionStorage.getItem('userCredentials') || '{}');
  if (userCreds?.token) {
    config.headers.Authorization = `Bearer ${userCreds.token}`;
  }
  return config;
});

// ── Response interceptor: normalize errors ────────────────────────────────────
apiClient.interceptors.response.use(
  (res) => res.data,
  (err) => {
    const message = err.response?.data?.detail || err.response?.data?.message || err.message;
    return Promise.reject(new Error(message));
  }
);

export default apiClient;

// ─────────────────────────────────────────────────────────────────────────────
// METADATA API
// ─────────────────────────────────────────────────────────────────────────────

export const metadataApi = {
  /**
   * Fetch complete workbook metadata (sheets, columns, permissions, toolbar).
   */
  fetchWorkbookMetadata(workbookId) {
    return apiClient.get(`/spreadsheet/workbooks/${workbookId}/metadata/`);
  },

  /**
   * Fetch metadata for a single sheet.
   */
  fetchSheetMetadata(workbookId, sheetId) {
    return apiClient.get(`/spreadsheet/workbooks/${workbookId}/sheets/${sheetId}/metadata/`);
  },

  /**
   * List all workbooks available to the current user.
   */
  listWorkbooks(entityId) {
    return apiClient.get('/spreadsheet/workbooks/', { params: { entity_id: entityId } });
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// WORKBOOK / SHEET DATA API
// ─────────────────────────────────────────────────────────────────────────────

export const workbookApi = {
  /**
   * Fetch all row data for a sheet (paginated for large datasets).
   */
  fetchSheetData(workbookId, sheetId, params = {}) {
    return apiClient.get(`/spreadsheet/workbooks/${workbookId}/sheets/${sheetId}/data/`, {
      params: { page_size: 500, ...params },
    });
  },

  /**
   * Save dirty rows (upsert — backend decides insert vs update by id).
   * Integrates with your project's existing save pattern.
   */
  saveRows(workbookId, sheetId, rows) {
    return apiClient.post(`/spreadsheet/workbooks/${workbookId}/sheets/${sheetId}/save/`, {
      rows,
    });
  },

  /**
   * Save a single row.
   */
  saveRow(workbookId, sheetId, row) {
    return workbookApi.saveRows(workbookId, sheetId, [row]);
  },

  /**
   * Submit a sheet for approval workflow.
   */
  submitSheet(workbookId, sheetId, payload = {}) {
    return apiClient.post(`/spreadsheet/workbooks/${workbookId}/sheets/${sheetId}/submit/`, payload);
  },

  /**
   * Approve a sheet (requires canApprove permission).
   */
  approveSheet(workbookId, sheetId, payload = {}) {
    return apiClient.post(`/spreadsheet/workbooks/${workbookId}/sheets/${sheetId}/approve/`, payload);
  },

  /**
   * Delete rows by id.
   */
  deleteRows(workbookId, sheetId, rowIds) {
    return apiClient.delete(`/spreadsheet/workbooks/${workbookId}/sheets/${sheetId}/rows/`, {
      data: { row_ids: rowIds },
    });
  },

  /**
   * Export sheet data (returns blob).
   */
  exportSheet(workbookId, sheetId, format = 'xlsx') {
    return apiClient.get(`/spreadsheet/workbooks/${workbookId}/sheets/${sheetId}/export/`, {
      params: { format },
      responseType: 'blob',
    });
  },

  /**
   * Fetch audit log for a sheet.
   */
  fetchAuditLog(workbookId, sheetId, params = {}) {
    return apiClient.get(`/spreadsheet/workbooks/${workbookId}/sheets/${sheetId}/audit/`, { params });
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// FORMULA API — server-side formula evaluation fallback
// ─────────────────────────────────────────────────────────────────────────────

export const formulaApi = {
  /**
   * Server-side formula evaluation (fallback when worker is unavailable).
   */
  evaluateFormula(formula, context) {
    return apiClient.post('/spreadsheet/formula/evaluate/', { formula, context });
  },

  /**
   * Validate a formula string server-side.
   */
  validateFormula(formula) {
    return apiClient.post('/spreadsheet/formula/validate/', { formula });
  },
};
